import TableItems from "components/Table/Table";
import Card from "components/card/Card";
import {
  DashboardItemsResponseDto,
  PropertyInfo,
  useCheckCreditScoreQuery,
  useGetDashboardItemsQuery,
  useSetStatusFlagOfUserMutation,
  useSetPalStatusFlagOfUserMutation,
  useCreateDashboardSessionMutation,
  PendingItem,
  useGetNeedsListV1Query,
  useCreateNeedsMutation,
  useUpdatePartnerSessionMutation,
  NeedType,
  useGetNeedsQuery,
} from "./dashboardService";
import FundedState from "components/funded-state/FundedState";
import LoanProgessBar from "components/ui/loan-progress-bar/LoanProgessBar";
import { useSelector, useDispatch } from "react-redux";
import DashboardPopUp from "pages/dashboard-popup/Dashboard-popup";
import "./dashboard.scss";
import {
  getCurrentUserId,
  getCurrentUserName,
  getDealApplicationId,
  getHasPAL,
  getIsInterimDashboard,
  getLoanPurpose,
  getNhDealApplicationId,
  getPartnerName,
  getProspectId,
  getSelfReferralInSession,
  setIsAlertVisibility,
  setLogout,
  getCloseAttentionBanner,
  setPartnerSession,
  getUserRole,
} from "features/authSlice";
import { useNavigate } from "react-router";
import { showDocuments } from "utilities/UserversionFeatures";
import { useEffect, useMemo, useState } from "react";
import NafInsuranceAd from "../advertisements/NafInsurance";
import { FeatureFlag, useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  DefaultConditionsCount,
  LoanPurposeNames,
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
  DASHBOARD_CONDITION_TILE_ERROR,
  DASHBOARD_CONDITION_TILE_HEADER,
  DASHBOARD_CREATE_NEEDS_TRIGGERED,
  DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE,
  DASHBOARD_CONDITION_NEEDS_ATTENTION,
  ESIGN_STATUS_UNDER_REVIEW,
  ESIGN_STATUS_COMPLETED,
} from "utilities/Constant";
import PreApprovalPreview from "./PalPreview/PreApprovalPreview";
import UqualBanner from "../advertisements/uqual-banner/UqualBanner";
import UqualModal from "../advertisements/uqual-modal/UqualModal";
import { disableDocumentUpload, enableDocumentUpload } from "../store/dashboardSlice";
import GearErrorImg from "assets/images/gear-error.png";
import { Loader } from "components/Loader/Loader";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import AttentionBanner from "../attentionBanner/AttentionBanner";
import useDesktopChecker from "hooks/useDesktopChecker";
import ESignUnderReviewModal from "pages/e-sign/ESignUnderReviewModal";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import Modal from "components/modal/Modal";
import NpsSurvey from "pages/survey/nps";
import { RegistrationRoleType } from "pages/registration/registrationService";


export const tooltipIndexValues = {
  initial: 0,
  conditions: 1,
  milestones: 2,
  documents: 3,
  completed: 4,
};

export default function Dashboard() {
  const userName = useSelector(getCurrentUserName);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const [isPalPreview, setIsPalPreview] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const isInterimDashboard = useSelector(getIsInterimDashboard);
  const { data: featureFlag, isLoading: isfeatureFlagLoading } = useGetFeatureFlagsQuery();
  const [shouldFetchNeedsList, setShouldFetchNeedsList] = useState(false);
  const isDesktop = useDesktopChecker();
  const isAttentionBannerClosed = useSelector(getCloseAttentionBanner);
  const [showUnderReviewModal, setShowUnderReviewModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showNpsModal, setShowNpsModal] = useState(false);

  const getOrCreateTabSession = () => {
    let dashboardSessionId = sessionStorage.getItem(DASHBOARD_SESSION_ID);
    let dashboardSessionStartTimestamp = sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP);

    if (!dashboardSessionId) {
      dashboardSessionId = crypto.randomUUID();
      dashboardSessionStartTimestamp = new Date().toISOString();
      sessionStorage.setItem(DASHBOARD_SESSION_ID, dashboardSessionId);
      sessionStorage.setItem(DASHBOARD_SESSION_START_TIMESTAMP, dashboardSessionStartTimestamp);
    }

    return {
      dashboardSessionId,
      dashboardSessionStartTimestamp,
      dashboardSessionEndTimestamp: null,
    };
  };
  const [createDashboardSession] = useCreateDashboardSessionMutation();

  const partnerAdId = useSelector(getPartnerName);
  const [updatePartnerSession] = useUpdatePartnerSessionMutation();
  const [isDashboardSessionCreated, setDashboardSessionCreated] = useState(false);

  // Memoize getOrCreateTabSession() so it's only called once per render
  const dashboardSession = useMemo(() => getOrCreateTabSession(), []);

  const { data, isError, error } = useGetDashboardItemsQuery({ dealApplicationId: nhDealApplicationId, userId: userId });
  const [createNeeds, { data: createNeedsResponse, isLoading: isCreatingNeeds, isSuccess: isCreateNeedsSucceeded }] = useCreateNeedsMutation();

  useEffect(() => {
    if (isfeatureFlagLoading || !data || isCreatingNeeds) return;
    const hasCreateNeedsTriggeredForSession = sessionStorage.getItem(DASHBOARD_CREATE_NEEDS_TRIGGERED) === DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE;

    const shouldEvaluateNeedsCreation = (data?.isAwaitingNeedsCreation === true || (isInterimDashboard && data?.isNew && data?.isCoborrowerNew)) && !hasCreateNeedsTriggeredForSession;

    if (shouldEvaluateNeedsCreation) {
      // If application form is not required, skip needs creation and fetch needs directly
      if (!data?.isApplicationFormRequired) {
        setShouldFetchNeedsList(true);
      } else if (!isCreateNeedsSucceeded && !shouldFetchNeedsList) {
        // Application form is required — trigger needs creation
        void createNeeds({ dealApplicationId, userId, isInterimDashboard });
        sessionStorage.setItem(DASHBOARD_CREATE_NEEDS_TRIGGERED, DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE);
      } 
    } else {
      setShouldFetchNeedsList(true);
    }
  }, [data?.isNew, data?.isCoborrowerNew, isCreatingNeeds]);

  // Separate effect to flip the flag AFTER needs creation is successful
  useEffect(() => {
    if (isCreateNeedsSucceeded && !shouldFetchNeedsList) {
      setShouldFetchNeedsList(true);
    }
  }, [isCreateNeedsSucceeded]);

  const [needsList, setNeedsList] = useState<PendingItem[]>([]);
  const [isNeedsListFailure, setIsNeedsListFailure] = useState<boolean>(false);

  const { data: pendingItemsV1, 
    isLoading: isNeedsListLoading,
    error: pendingItemsV1Error,
    refetch: refetchNeedsListV1,} = useGetNeedsListV1Query(
    { nhDealApplicationId: nhDealApplicationId, userId: useSelector(getCurrentUserId) },
    { skip: isfeatureFlagLoading }
  );

  const hasNeedsAttention = needsList.length > 0 && needsList.some((item) => item.status === DASHBOARD_CONDITION_NEEDS_ATTENTION);

  useEffect(() => {
    setNeedsList(pendingItemsV1 ?? []);
  }, [pendingItemsV1]);

  useEffect(() => {
    if (!userId || !nhDealApplicationId) return;

    const sessionKey = `dashboardSession_${userId}_${nhDealApplicationId}`;
    const existingSession = sessionStorage.getItem(sessionKey);

    if (!existingSession) {
      void createDashboardSession({
        nhDealApplicationId,
        userId,
        dashboardSession,
      });
      sessionStorage.setItem(sessionKey, "true");
      setDashboardSessionCreated(true);
    } else {
      setDashboardSessionCreated(true);
    }
  }, [userId, nhDealApplicationId]);

  useEffect(() => {
    if (!needsList) return;

    if (hasNeedsAttention) {
      if (isAttentionBannerClosed === false) {
        dispatch(setIsAlertVisibility(true));
      }
    }
  }, [needsList]);

  useEffect(() => {
    if (isDashboardSessionCreated && partnerAdId) {
      void updatePartnerSession({
        nhDealApplicationId,
        userId,
        partnerAdId,
      });
    }
  }, [isDashboardSessionCreated, partnerAdId]);

  useEffect(() => {
    if (
      pendingItemsV1Error ||
      (isInterimDashboard && data?.isNew && data?.isCoborrowerNew && (!isCreateNeedsSucceeded || !createNeedsResponse))
    ) {
      setIsNeedsListFailure(true);
      dispatch(disableDocumentUpload());
    } else {
      dispatch(enableDocumentUpload());
    }
  }, [pendingItemsV1Error, !isCreateNeedsSucceeded, !createNeedsResponse]);

  useEffect(() => {
    if (showCompletedModal) {
      const timer = setTimeout(() => setShowCompletedModal(false), 3000); //turn off the flag to avoid repeat toasters
      return () => clearTimeout(timer);
    }
  }, [showCompletedModal]);

  const checkEdisclosureStatus = (currentNeed: PendingItem) => {
    void refetchNeedsListV1().then((result) => {
      const refreshedNeeds = result?.data ?? [];
      const currentNeedLatestData = refreshedNeeds.find(
        (need) =>
          need.packageId == currentNeed.packageId &&
          ((need.prospectId && need.prospectId == currentNeed.prospectId) || (need.nboContactId && need.nboContactId == currentNeed.nboContactId))
      );

      if (!currentNeedLatestData) return;

      if (currentNeedLatestData.needType === NeedType.ESign) {
        const status = currentNeedLatestData.status?.toLowerCase();
        if (status === ESIGN_STATUS_UNDER_REVIEW) {
          setShowUnderReviewModal(true);
        } else if (status === ESIGN_STATUS_COMPLETED) {
          setShowCompletedModal(true);
        }
      }
    });
  };

  const [tooltipIndex, setTooltipIndex] = useState(tooltipIndexValues.initial);

  const toggleModal = () => setIsPalPreview(!isPalPreview);
  let component: JSX.Element | null = null;
  if (isError && typeof error === "object" && "status" in error && error.status === 401) {
    dispatch(setLogout());
    navigate("/", { replace: true, state: { path: location.pathname } });
  }
  if (data == undefined) {
    return null;
  }

  const {
    loanStatus: { status, subStatus },
    closingDate,
    propertyInfo,
  }: DashboardItemsResponseDto = data;

  const handleClickPal = () => {
    toggleModal();
    setTooltipIndex(tooltipIndexValues.completed);
  };

  const handleClosePal = () => {
    toggleModal();
    setTooltipIndex(tooltipIndexValues.conditions);
  };

  const closeNpsModal = () => {
    setShowNpsModal(false);
  };

  switch (status) {
    case "Processing":
    case "Underwriting":
    case "Approval":
    case "FinalReview":
    case "Close":
    default:
      component = (
        <>
          {showUnderReviewModal && (
            <div className="dashboard-esign-under-review-modal">
              <Modal
                customBody={<ESignUnderReviewModal handleClose={() => setShowUnderReviewModal(false)} />}
                defaultBody={false}
                defaultHeader={false}
              />
            </div>
          )}

          {showCompletedModal && <ToastMessage header="Completed" message={`E-sign task completed.`} state="success" position="top-right" />}
          <OutStandingItemsCard
            tooltipIndex={tooltipIndex}
            setTooltipIndex={setTooltipIndex}
            isStatusInStarted={status == "Started"}
            handleClickPal={handleClickPal}
            isPalPreview={isPalPreview}
            propertyInfo={propertyInfo}
            featureFlag={featureFlag}
            needsList={needsList}
            isNeedsListFailure={isNeedsListFailure}
            isCreatingNeeds={isCreatingNeeds}
            isNeedsListLoading={isNeedsListLoading}
            isDesktop={isDesktop}
            checkEdisclosureStatus={checkEdisclosureStatus}
          />
        </>
      );
      break;
    case "Funded":
      component = <FundedState userName={userName.toUpperCase() + "!"} />;
      break;
  }
  return (
    <>
      {isCreatingNeeds && <Loader message1="One moment!" message2="Getting your latest loan updates." />}
      {!isCreatingNeeds && isNeedsListLoading && (
        <div className="dashboard-overlay">
          <Overlay />
        </div>
      )}

      {status != "Funded" && (
        <LoanProgessBar
          status={status}
          currentStatus={subStatus}
          closingDate={closingDate}
          showTooltip={tooltipIndex === tooltipIndexValues.milestones}
          tooltipIndex={tooltipIndex}
          setTooltipIndex={setTooltipIndex}
        />
      )}
      {component}
      {isPalPreview && <PreApprovalPreview onClose={handleClosePal} />}
    </>
  );
}

const OutStandingItemsCard = (props: {
  tooltipIndex: number;
  setTooltipIndex: (index: number) => void;
  isStatusInStarted: boolean;
  handleClickPal: () => void;
  isPalPreview?: boolean;
  propertyInfo?: PropertyInfo;
  featureFlag: FeatureFlag | undefined;
  needsList: PendingItem[];
  isNeedsListFailure: boolean;
  isCreatingNeeds: boolean;
  isNeedsListLoading: boolean;
  isDesktop: boolean;
  checkEdisclosureStatus?: (currentNeed: PendingItem) => void;
}) => {
  const {
    tooltipIndex,
    setTooltipIndex,
    isStatusInStarted,
    handleClickPal,
    isPalPreview = false,
    propertyInfo,
    featureFlag,
    needsList,
    isNeedsListFailure,
    isCreatingNeeds,
    isNeedsListLoading,
    isDesktop,
    checkEdisclosureStatus,
  } = props;
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const hasPAL = useSelector(getHasPAL);
  const userName = useSelector(getCurrentUserName);
  const prospectId = useSelector(getProspectId);
  const selfReferralInSession = useSelector(getSelfReferralInSession);

  const { isUserNew, isCoborrowerNew, isGetItemError, getItemError, isPalViewed, isUqualSelfReferralDone } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId: useSelector(getCurrentUserId) },
    {
      selectFromResult: ({ data, isError, error }) => ({
        isUserNew: data!.isNew,
        isCoborrowerNew: data!.isCoborrowerNew,
        isGetItemError: isError,
        getItemError: error,
        isPalViewed: data!.isPalViewed,
        isUqualSelfReferralDone: data?.isUqualSelfReferralDone,
      }),
    }
  );

  const { data: isCreditScoreLow } = useCheckCreditScoreQuery({ nhDealApplicationId: nhDealApplicationId, prospectId: prospectId });
  const [updateUserstatus, { isError, error }] = useSetStatusFlagOfUserMutation();
  const [updatePalUserStatus] = useSetPalStatusFlagOfUserMutation();
  const loanPurpose = useSelector(getLoanPurpose);
  const [isUqualSessionCreated, setUqualSessionCreated] = useState(false);
  const [hasNpsSurveyBeenRendered, setHasNpsSurveyBeenRendered] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  if (isGetItemError || isError) {
    if (typeof getItemError === "object" && "status" in getItemError && getItemError.status === 401) {
      dispatch(setLogout());
      navigate("/", { replace: true, state: { path: location.pathname } });
    }
    if (typeof error === "object" && "status" in error && error.status === 401) {
      dispatch(setLogout());
      navigate("/", { replace: true, state: { path: location.pathname } });
    }
    // should add code for other errors
  }
  const userId = useSelector(getCurrentUserId);
  const handleClose = () => {
    if (hasPAL) {
      void updatePalUserStatus(nhDealApplicationId);
    } else {
      void updateUserstatus({ id: nhDealApplicationId, userId: userId });
    }
    setTooltipIndex(tooltipIndexValues.conditions);
  };

  const handleClick = () => {
    handleClose();
    handleClickPal();
  };

  const shouldDisplayInsuranceAd = () => {
    const loanPurposeLower = loanPurpose.toLowerCase();
    const isRefinance = loanPurposeLower === LoanPurposeNames.Refinance.toLowerCase();
    const isPurchase = loanPurposeLower === LoanPurposeNames.Purchase.toLowerCase();
    const insuranceAdEnabled = featureFlag?.insuranceAdEnabled;
    const insuranceAdForSubPropEnabled = featureFlag?.insuranceAdForSubPropAddressEnabled;
    const hasValidStreet = propertyInfo?.street && !["", "TBD", "TBA"].includes(propertyInfo.street);
    if (!insuranceAdForSubPropEnabled && insuranceAdEnabled && isCreditScoreLow === false) return true;

    if (hasValidStreet) {
      if (insuranceAdEnabled && !isCreditScoreLow) {
        if (insuranceAdForSubPropEnabled && isRefinance) return true;
        if (isPurchase && !hasValidStreet) return false;
        if (hasValidStreet) return true;
      }

      if (
        insuranceAdEnabled &&
        isCreditScoreLow &&
        isUqualSelfReferralDone &&
        !selfReferralInSession &&
        featureFlag?.uQualSelfReferralAdLogicEnabled
      ) {
        return true;
      }
    }
    return false;
  };

  const isDisplayUqualAdBanner = (): boolean => {
    if (!isCreditScoreLow) return false;

    if (featureFlag?.uQualSelfReferralAdLogicEnabled) {
      return !isUqualSelfReferralDone || selfReferralInSession;
    }
    return true;
  };

  const { data } = useGetDashboardItemsQuery({ dealApplicationId: nhDealApplicationId, userId: useSelector(getCurrentUserId) });

  const userRole = useSelector(getUserRole);
  const userRoleNormalized = (userRole || "").toLowerCase(); // value can be  unknown, borrower, spousalcoborrower, nonspousalcoborrower

  useEffect(() => {
    const shouldRunForBorrower =
    userRoleNormalized === RegistrationRoleType.Borrower.toLowerCase() &&
    data?.isNew;

    const shouldRunForSpouse =
    userRoleNormalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase() &&
    data?.isCoborrowerNew;

    if (!featureFlag || (!shouldRunForBorrower && !shouldRunForSpouse)) return;
    let partnerAdId: string | undefined = undefined;

    // Check Uqual first
    if (isCreditScoreLow && !isUqualSelfReferralDone && !isUqualSessionCreated) {
      partnerAdId = PartnerAdId[PartnerAdId.Uqual];
      setUqualSessionCreated(true);
    }
    // Check Insurance Ad only if needed fields exist
    else if (loanPurpose && propertyInfo !== undefined && shouldDisplayInsuranceAd()) {
      partnerAdId = PartnerAdId[PartnerAdId.NafInsuranceMatic];
    }

    if (partnerAdId) {
      dispatch(setPartnerSession(partnerAdId));
    }
  }, [
    data?.isNew,
    data?.isCoborrowerNew,
    loanPurpose,
    featureFlag?.insuranceAdEnabled,
    featureFlag?.insuranceAdForSubPropAddressEnabled,
    featureFlag?.uQualSelfReferralAdLogicEnabled,
    propertyInfo?.street,
    isCreditScoreLow,
    isUqualSelfReferralDone,
    selfReferralInSession,
    isUqualSessionCreated,
  ]);

  useEffect(() => {
    if (featureFlag?.npsSurveyEnabled && isNeedsListLoading && !hasNpsSurveyBeenRendered) {
      setHasNpsSurveyBeenRendered(true);
    }
  }, [featureFlag?.npsSurveyEnabled, isNeedsListLoading, hasNpsSurveyBeenRendered]);

  const loanstatus = data?.loanStatus.status != "Started";
  const getConditionsTile = () => {
    return (
      <>
        {isNeedsListFailure && (
          <div className="tasks-list-error">
            <hr />
            <div className="image-text-container">
              <img src={GearErrorImg} alt={DASHBOARD_CONDITION_TILE_ERROR} />
              {DASHBOARD_CONDITION_TILE_ERROR}
            </div>
          </div>
        )}
        {!isNeedsListFailure && (
          <>
            <TableItems
              Tablevalues={needsList}
              showTooltip={tooltipIndex === tooltipIndexValues.conditions}
              setTooltipIndex={setTooltipIndex}
              isShowAllConditions={isStatusInStarted}
              checkEdisclosureStatus={checkEdisclosureStatus}
            />
            {shouldDisplayInsuranceAd() && <NafInsuranceAd />}
          </>
        )}
      </>
    );
  };

  const [isShowUqualModal, setIsShowUqualModal] = useState(false);
  const handleUqualClick = () => {
    setIsShowUqualModal(true);
  };

  const getCardHeader = (): string => {
    return isNeedsListFailure || tooltipIndex !== tooltipIndexValues.conditions ? DASHBOARD_CONDITION_TILE_HEADER : "";
  };

  return (
    <>
      {hasNpsSurveyBeenRendered && <NpsSurvey />}
      {isDisplayUqualAdBanner() && (
        <div className="dashboard-uqal-banner">
          <UqualBanner onClick={handleUqualClick} />
        </div>
      )}
      <div>
        {isDesktop && needsList.some((item) => item.status === "needsAttention") && <AttentionBanner />}
      </div>
      {isCreditScoreLow && isShowUqualModal && <UqualModal onClose={() => setIsShowUqualModal(false)} />}
      <div className={loanstatus || needsList.length == DefaultConditionsCount ? "generic-condition" : "conditionsContainer"}>
        <Card header={getCardHeader()} body={getConditionsTile()} />
      </div>
      {showDocuments() && (
        <div className="dashboard-content">
          <p className="dashboard-content__text">* All documents uploaded by you are visible under the My Documents tab</p>
        </div>
      )}
      {!isPalPreview &&
        ((isUserNew && isCoborrowerNew) || (!isPalViewed && hasPAL && featureFlag?.preApprovalLetterEnabled)) &&
        (!isCreatingNeeds || !isNeedsListLoading) && (
          <DashboardPopUp
            onClose={() => {
              handleClose();
            }}
            userName={userName.split(" ")[0]}
            showNewPalPopup={!isPalViewed && hasPAL}
            handleClick={handleClick}
          />
        )}
    </>
  );
};

export enum PartnerAdId {
  None = 0,
  NafInsuranceMatic = 1,
  Uqual = 2,
}
